import { PROFILE_OF_USER } from 'mk/autogenerated/translations/ProfileProductsPage.5da7d8012dffab7c111b9aed167846f2'
import { AvatarGridType } from 'mk2/apps/blogs/components/MoreAboutMe';
import ProfileSideMenu from 'mk2/apps/blogs/components/ProfileSideMenu';
import {
    profileDeleteAvatarByAdminTrigger,
    profileDetailFetchTrigger,
    profileExpandAvatarGridTrigger,
    profileExpandGroupListTrigger,
    profileSetLikeUserTrigger,
} from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.actions';
import profileDetailReducer, {
    ProfileDetailData,
    ProfileDetailState,
} from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.reducers';
import profileDetailSaga from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.sagas';
import { getProfileDetailState } from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.selectors';
import ProfileProducts from 'mk2/apps/blogs/containers/ProfileProducts/ProfileProducts';
import {
    profileProductsAddProduct,
    profileProductsRemoveProduct,
    profileProductsSearchProducts,
    SuggestedStrollerOption,
} from 'mk2/apps/blogs/containers/ProfileProducts/ProfileProducts.actions';
import reducer from 'mk2/apps/blogs/containers/ProfileProducts/ProfileProducts.reducers';
import saga from 'mk2/apps/blogs/containers/ProfileProducts/ProfileProducts.sagas';
import {
    getProfileProductsPerProfileState,
    getProfileProductsState,
} from 'mk2/apps/blogs/containers/ProfileProducts/ProfileProducts.selectors';
import { blogsProfileUrl } from 'mk2/apps/blogs/urls';
import { KidEntity, KidSchema } from 'mk2/apps/settings/schemas';
import { AppName } from 'mk2/containers/Application/Application';
import { cacheLast } from 'mk2/helpers/cache';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page, { BrandingProps, PageLayout, RightContentSide } from 'mk2/pages/Page';
import { getRequestDeviceMobile, getRequestPermissions, getRequestUser, AppState } from 'mk2/reducers';
import { isFailure, LoadingState } from 'mk2/reducers/loadingState';
import {
    GroupEntity,
    GroupSchema,
    MenuSelection,
    PhotoEntity,
    PhotoSchema,
    ProfileProductType,
    ProVendorEntity,
    ProVendorSchema,
    StrollerEntity,
    StrollerSchema,
    StrollerUserReviewEntity,
    StrollerUserReviewSchema,
    UserEntity,
    UserSchema,
} from 'mk2/schemas';
import { getDenormalizedEntities, getDenormalizedEntity } from 'mk2/selectors';
import { Store } from 'mk2/store/configureStore';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';

// TODO: Refactor to be more generic - prepared for more types of products
// TODO: Move "from ProfileDetail" features to own container

interface OwnProps {}

interface StateProps {
    isMobile: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    addingLoadingState: LoadingState;

    // From ProfileDetail
    detailErrorMessage: string;
    detailLoadingState: LoadingState;
    profileDetailData: ProfileDetailData;
    suggestedStrollerOptions: SuggestedStrollerOption[];
}

interface DispatchProps {
    onRemoveProduct(username: string, productType: ProfileProductType, productId: number);
    onAddProduct(username: string, productType: ProfileProductType, productId: number);
    onSearchProducts(productType: ProfileProductType, term: string);

    // From ProfileDetail
    onLoadProfileDetail(username: string);
    onSetLikeUser(username: string, like: boolean);
    onDeleteAvatarByAdmin(username: string);
    onExpandAvatarGrid(avatarGridType: AvatarGridType, username: string);
    onExpandGroupList(username: string);
}

type RouteProps = RouteComponentProps<{
    username: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class ProfileProductsPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {
    private profileDetailDataCache = cacheLast();

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const requestUserPermissions = getRequestPermissions(state);
            const requestUser = getRequestUser(state);

            const username = ownProps.match.params.username;
            const profileState: ProfileDetailState = getProfileDetailState(state, username);
            const profileDetailData = profileState.profileDetailData;
            const profileProductsState = getProfileProductsState(state);
            const profileProductsProfileState = getProfileProductsPerProfileState(state, username);

            const user: UserEntity = getDenormalizedEntity<UserEntity>(state, UserSchema, profileDetailData.user);

            const userVendorData: ProVendorEntity = profileDetailData.userVendorData
                ? getDenormalizedEntity<ProVendorEntity>(state, ProVendorSchema, profileDetailData.userVendorData)
                : null;

            const profileDetailDataDenormalized = {
                ambassadorBrandGroupId: profileDetailData.ambassadorBrandGroupId,
                ambassadorBrandLogo: getDenormalizedEntity<PhotoEntity>(
                    state,
                    PhotoSchema,
                    profileDetailData.ambassadorBrandLogo,
                ),
                ambassadorBrandName: profileDetailData.ambassadorBrandName,
                displayVisibilityAlert: profileDetailData.displayVisibilityAlert,
                followers: {
                    count: profileDetailData.followers.count,
                    expanded: profileDetailData.followers.expanded,
                    hasMore: profileDetailData.followers.hasMore,
                    users: getDenormalizedEntities<UserEntity>(state, UserSchema, profileDetailData.followers.users),
                },
                following: {
                    count: profileDetailData.following.count,
                    expanded: profileDetailData.following.expanded,
                    hasMore: profileDetailData.following.hasMore,
                    users: getDenormalizedEntities<UserEntity>(state, UserSchema, profileDetailData.following.users),
                },
                isLegacyBlocked: profileDetailData.isLegacyBlocked,
                kids: getDenormalizedEntities<KidEntity>(state, KidSchema, profileDetailData.kids),
                memberOfGroups: {
                    count: profileDetailData.memberOfGroups.count,
                    hasMore: profileDetailData.memberOfGroups.hasMore,
                    groups: getDenormalizedEntities<GroupEntity>(
                        state,
                        GroupSchema,
                        profileDetailData.memberOfGroups.groups,
                    ),
                },
                pregnancyDaysCounter: profileDetailData.pregnancyDaysCounter,
                pregnancyWeekNumber: profileDetailData.pregnancyWeekNumber,
                user,
                userAboutMeText: profileDetailData.userAboutMeText,
                userAge: profileDetailData.userAge,
                userAvatarPhoto: getDenormalizedEntity<PhotoEntity>(
                    state,
                    PhotoSchema,
                    profileDetailData.userAvatarPhoto,
                ),
                userIsInactive: profileDetailData.userIsInactive,
                userIsLiked: profileDetailData.userIsLiked,
                userOnlineStatus: profileDetailData.userOnlineStatus,
                userRegisteredOn: profileDetailData.userRegisteredOn,
                userResidence: profileDetailData.userResidence,
                userResidenceCountyId: profileDetailData.userResidenceCountyId,
                userStats: profileDetailData.userStats,
                userStrollers: getDenormalizedEntities<StrollerEntity>(
                    state,
                    StrollerSchema,
                    profileDetailData.userStrollers,
                ),
                userStrollersReviews: getDenormalizedEntities<StrollerUserReviewEntity>(
                    state,
                    StrollerUserReviewSchema,
                    profileDetailData.userStrollersReviews,
                ),
                userVendorData,
            };

            return {
                isMobile: getRequestDeviceMobile(state),
                requestUser,
                requestUserPermissions,

                // From ProfileDetail
                detailErrorMessage: profileState.detailErrorMessage,
                detailLoadingState: profileState.detailLoadingState,
                profileDetailData: this.profileDetailDataCache(
                    profileDetailDataDenormalized,
                    ...Object.values(profileDetailData),
                ),
                suggestedStrollerOptions: profileProductsState.suggestedStrollerOptions,
                addingLoadingState: profileProductsProfileState.addingLoadingState,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onRemoveProduct: profileProductsRemoveProduct,
            onAddProduct: profileProductsAddProduct,
            onSearchProducts: profileProductsSearchProducts,

            // From ProfileDetail
            onLoadProfileDetail: profileDetailFetchTrigger,
            onSetLikeUser: profileSetLikeUserTrigger,
            onDeleteAvatarByAdmin: profileDeleteAvatarByAdminTrigger,
            onExpandAvatarGrid: profileExpandAvatarGridTrigger,
            onExpandGroupList: profileExpandGroupListTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const {
            match: {
                params: { username },
            },
        } = props;
        return `${PROFILE_OF_USER} ${username}`;
    }

    public render(props: Props): false | JSX.Element {
        return <ProfileProducts {...props} />;
    }

    public renderRight(props: Props): JSX.Element {
        const {
            detailLoadingState,
            isMobile,
            location,
            match: {
                params: { username },
            },
            profileDetailData,
            requestUser,
            requestUserPermissions,
            onDeleteAvatarByAdmin,
            onExpandAvatarGrid,
            onExpandGroupList,
            onLoadProfileDetail,
            onSetLikeUser,
        } = props;

        return isMobile ||
            (profileDetailData.user && profileDetailData.user.isBlocked) ||
            isFailure(detailLoadingState) ? null : (
            <ProfileSideMenu
                isMobile={isMobile}
                location={location}
                loadingState={detailLoadingState}
                onDeleteAvatarByAdmin={onDeleteAvatarByAdmin}
                onExpandAvatarGrid={onExpandAvatarGrid}
                onExpandGroupList={onExpandGroupList}
                onLoadProfileDetail={onLoadProfileDetail}
                onSetLikeUser={onSetLikeUser}
                profileDetailData={profileDetailData}
                requestUser={requestUser}
                requestUserPermissions={requestUserPermissions}
                username={username}
            />
        );
    }

    public getPageLayout(app: AppName, props: Props): PageLayout {
        const { detailLoadingState, profileDetailData } = props;
        return (profileDetailData.user && profileDetailData.user.isBlocked) || isFailure(detailLoadingState)
            ? PageLayout.MainContent
            : PageLayout.MainContentWithRightColumn;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.LeftSide;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return url(blogsProfileUrl, { username: props.match.params.username });
    }

    public getHeaderMenuSelection(app: AppName, props: Props): MenuSelection {
        return {
            activeSection: 'photoblog',
            activeItem: null,
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        return {
            zone: 'Blogs - Branding',
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.blogs.profileDetails', profileDetailReducer);
        store.injectSaga('containers.blogs.profileDetails', profileDetailSaga);
        store.injectReducer('containers.blogs.profileProducts', reducer);
        store.injectSaga('containers.blogs.profileProducts', saga);
    }
}
